import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  ShieldCheck,
  ArrowRight,
  MapPin,
  X,
} from "lucide-react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { Colors } from "../constants/Colors";
import { useAuth } from "../context/AuthContext";
import "../styles/login.css";
import api from "../services/api";

const MySwal = withReactContent(Swal);

const swalClasses = {
  popup: "swal-custom-popup",
  title: "swal-custom-title",
  content: "swal-custom-content",
};

const OTP_LENGTH = 6;
const RESEND_SECONDS = 600;

const formatTime = (s) =>
  `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [loginToken, setLoginToken] = useState("");
  const [countdown, setCountdown] = useState(0);

  const otpInputRef = useRef(null);
  const [otpFocused, setOtpFocused] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  /* ---------- Alerts ---------- */
  const showErrorAlert = (title, message) =>
    MySwal.fire({
      title: title || "Sign in failed",
      text: message,
      icon: "error",
      confirmButtonText: "Try again",
      confirmButtonColor: Colors.GRADIENT_START,
      customClass: swalClasses,
    });

  const showSuccessAlert = () =>
    MySwal.fire({
      title: "Welcome back",
      text: "Signed in. Opening your dashboard…",
      icon: "success",
      timer: 1500,
      showConfirmButton: false,
      customClass: swalClasses,
    });

  const showInfoAlert = (title, message) =>
    MySwal.fire({
      title,
      text: message,
      icon: "info",
      confirmButtonText: "OK",
      confirmButtonColor: Colors.GRADIENT_START,
      customClass: swalClasses,
    });

  /* ---------- Session-expired notice (from api.js redirect) ---------- */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("expired") === "1") {
      showInfoAlert(
        "Session expired",
        "Your session ended for security. Please sign in again."
      );
      // Clean the URL so a refresh doesn't re-trigger the alert
      window.history.replaceState({}, "", location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- Countdown ---------- */
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  /* ---------- Modal: focus + Escape to close ---------- */
  useEffect(() => {
    if (!showOtpModal) return;
    otpInputRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape" && !otpLoading) closeOtpModal();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOtpModal, otpLoading]);

  /* ---------- Step 1: request code ---------- */
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post("/admin/request-login-otp", {
        email,
        password,
      });

      if (response.status === 200) {
        setLoginToken(response.data.login_token);
        setShowOtpModal(true);
        setCountdown(RESEND_SECONDS);
      } else {
        throw new Error(
          response.data.message || "Failed to send verification code"
        );
      }
    } catch (err) {
      // 429 — rate limited by backend
      if (err.response?.status === 429) {
        const wait = err.response.data?.retry_after || 60;
        showErrorAlert(
          "Too many attempts",
          err.response.data?.message ||
            `Please wait ${wait} seconds before trying again.`
        );
        return;
      }

      // 401 — bad credentials (generic message is intentional from backend)
      let msg = "Sign in failed. Please try again.";
      if (err.response?.data?.message) {
        msg = err.response.data.message;
      } else if (err.message?.includes("Network Error")) {
        msg = "Network error. Check your connection and try again.";
      }
      showErrorAlert("Sign in failed", msg);
    } finally {
      setLoading(false);
    }
  };

  /* ---------- Step 2: verify code ---------- */
  const handleVerifyOtp = async (e) => {
    if (e) e.preventDefault();

    if (otp.length !== OTP_LENGTH) {
      showErrorAlert(
        "Incomplete code",
        "Enter the 6-digit verification code."
      );
      return;
    }

    setOtpLoading(true);

    try {
      const response = await api.post("/admin/verify-login-otp", {
        login_token: loginToken,
        otp,
      });

      const { token, admin: adminData } = response.data;

      if (!token) {
        throw new Error("Authentication token not received");
      }

      localStorage.setItem("arkila_admin_token", token);

      if (adminData) {
        localStorage.setItem("arkila_admin_data", JSON.stringify(adminData));
      }

      await login({ token, admin: adminData });
      await showSuccessAlert();

      setEmail("");
      setPassword("");
      setOtp("");
      setShowOtpModal(false);

      const redirectTo = location.state?.from?.pathname || "/dashboard";
      navigate(redirectTo, { replace: true });
    } catch (err) {
      // 429 — too many verification attempts
      if (err.response?.status === 429) {
        showErrorAlert(
          "Too many attempts",
          err.response.data?.message ||
            "Please request a new code and try again later."
        );
        return;
      }

      // Session expired / invalid token → close modal and force restart
      const backendMsg = err.response?.data?.message || "";
      if (
        err.response?.status === 401 &&
        backendMsg.toLowerCase().includes("session")
      ) {
        showInfoAlert(
          "Session expired",
          "Your verification session expired. Please sign in again."
        );
        closeOtpModal();
        return;
      }

      // Invalid OTP — keep modal open, clear input, refocus
      showErrorAlert(
        "Invalid code",
        backendMsg || "Verification failed. Please try again."
      );
      setOtp("");
      otpInputRef.current?.focus();
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (countdown > 0) return;

    setOtpLoading(true);
    try {
      const response = await api.post("/admin/request-login-otp", {
        email,
        password,
      });

      if (response.status === 200) {
        setLoginToken(response.data.login_token);
        setCountdown(RESEND_SECONDS);
        setOtp("");
        otpInputRef.current?.focus();
        showInfoAlert(
          "Code sent",
          "A new code is on its way to your email."
        );
      } else {
        throw new Error(response.data.message || "Failed to resend code");
      }
    } catch (error) {
      if (error.response?.status === 429) {
        showErrorAlert(
          "Too many attempts",
          error.response.data?.message ||
            "Please wait before requesting another code."
        );
        return;
      }
      showErrorAlert(
        "Could not resend",
        error.response?.data?.message || error.message
      );
    } finally {
      setOtpLoading(false);
    }
  };

  const closeOtpModal = () => {
    setShowOtpModal(false);
    setOtp("");
    setCountdown(0);
  };

  /* ---------- OTP input handlers ---------- */
  const handleOtpChange = (e) => {
    setOtp(e.target.value.replace(/\D/g, "").slice(0, OTP_LENGTH));
  };

  const keepCaretAtEnd = (e) => {
    const el = e.target;
    const end = el.value.length;
    el.setSelectionRange(end, end);
  };

  /* ---------- Render ---------- */
  return (
    <div className="ark-root">
      {/* ============ LEFT — BRAND + ROUTE MAP ============ */}
      <aside className="ark-left" aria-hidden={false}>
        {/* Decorative map with last-known location markers */}
        <div className="ark-map" aria-hidden="true">
          <svg viewBox="0 0 600 800" preserveAspectRatio="xMidYMid slice">
            <g className="ark-streets">
              <path d="M-20 140 L620 100" />
              <path d="M-20 300 L620 330" />
              <path d="M-20 470 L620 440" />
              <path d="M-20 640 L620 680" />
              <path d="M110 -20 L90 820" />
              <path d="M290 -20 L320 820" />
              <path d="M480 -20 L500 820" />
            </g>

            <path
              id="ark-route"
              className="ark-route-glow"
              d="M70 700 C 170 650, 120 520, 240 480 S 400 450, 380 330 S 480 190, 540 130"
            />
            <path
              className="ark-route-line"
              d="M70 700 C 170 650, 120 520, 240 480 S 400 450, 380 330 S 480 190, 540 130"
            />

            <g className="ark-stops">
              <circle cx="70" cy="700" r="7" />
              <circle cx="240" cy="480" r="7" />
              <circle cx="380" cy="330" r="7" />
              <circle cx="540" cy="130" r="7" />
            </g>

            <g className="ark-pins">
              <circle className="ark-ping" cx="240" cy="480" r="7" />
              <circle className="ark-ping p2" cx="540" cy="130" r="7" />
            </g>
          </svg>
        </div>

        <div className="ark-brand-top">
          <div className="ark-logo-box">
            <img src="/ArkilaLogo.png" alt="Arkila logo" />
          </div>
          <div className="ark-brand-name">
            Arkila
            <span>Admin console</span>
          </div>
        </div>

        <div className="ark-hero">
          <h1 className="ark-title">
            Every jeepney, booking and driver in one place.
          </h1>
          <p className="ark-tagline">
            Manage rentals, drivers and your jeepneys' last known locations
            from a single dashboard.
          </p>

          {/* Decorative live card — copy is generic to avoid faking freshness */}
          <div className="ark-live-card">
            <span className="ark-live-icon">
              <MapPin size={18} />
            </span>
            <div>
              <strong>Fleet tracking</strong>
              <small>Live operator locations</small>
            </div>
          </div>
        </div>

        <p className="ark-note">
          <ShieldCheck size={15} />
          Admin access only. We'll email a one-time code each time you sign in.
        </p>
      </aside>

      {/* ============ RIGHT — FORM ============ */}
      <main className="ark-right">
        <div className="ark-form-wrapper">
          <div className="ark-form-header">
            <h2 className="ark-heading">Sign in</h2>
            <p className="ark-sub">
              Use your admin account. We'll email you a code to finish signing
              in.
            </p>
          </div>

          <form className="ark-form" onSubmit={handleLogin}>
            <div>
              <label className="ark-input-label" htmlFor="ark-email">
                Email address
              </label>
              <div className="ark-input-wrap">
                <Mail size={18} className="ark-input-icon" />
                <input
                  id="ark-email"
                  type="email"
                  className="ark-input"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  required
                  autoComplete="username"
                />
              </div>
            </div>

            <div>
              <label className="ark-input-label" htmlFor="ark-password">
                Password
              </label>
              <div className="ark-input-wrap">
                <Lock size={18} className="ark-input-icon" />
                <input
                  id="ark-password"
                  type={showPassword ? "text" : "password"}
                  className="ark-input"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="ark-eye"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                  aria-label={
                    showPassword ? "Hide password" : "Show password"
                  }
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button type="submit" className="ark-btn" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Sending code…
                </>
              ) : (
                <>
                  Send verification code
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>
        </div>
      </main>

      {/* ============ OTP VERIFICATION MODAL ============ */}
      {showOtpModal && (
        <div
          className="ark-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ark-otp-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !otpLoading) closeOtpModal();
          }}
        >
          <div className="ark-modal">
            <button
              type="button"
              className="ark-modal-close"
              onClick={closeOtpModal}
              disabled={otpLoading}
              aria-label="Close"
            >
              <X size={18} />
            </button>

            <div className="ark-modal-icon">
              <ShieldCheck size={26} />
            </div>

            <h3 id="ark-otp-title" className="ark-modal-title">
              Check your email
            </h3>
            <p className="ark-modal-text">
              We sent a 6-digit code to <strong>{email}</strong>. Enter it
              below to finish signing in.
            </p>

            <form onSubmit={handleVerifyOtp}>
              <div
                className="ark-otp"
                onClick={() => otpInputRef.current?.focus()}
              >
                <input
                  ref={otpInputRef}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="one-time-code"
                  className="ark-otp-input"
                  value={otp}
                  onChange={handleOtpChange}
                  onSelect={keepCaretAtEnd}
                  onFocus={() => setOtpFocused(true)}
                  onBlur={() => setOtpFocused(false)}
                  maxLength={OTP_LENGTH}
                  disabled={otpLoading}
                  aria-label="6-digit verification code"
                />
                {Array.from({ length: OTP_LENGTH }, (_, i) => (
                  <div
                    key={i}
                    aria-hidden="true"
                    className={`ark-otp-box ${otp[i] ? "filled" : ""} ${
                      otpFocused && i === Math.min(otp.length, OTP_LENGTH - 1)
                        ? "active"
                        : ""
                    }`}
                  >
                    {otp[i] || ""}
                  </div>
                ))}
              </div>

              <div className="ark-resend">
                {countdown > 0 ? (
                  <span>Resend available in {formatTime(countdown)}</span>
                ) : (
                  <button
                    type="button"
                    className="ark-resend-btn"
                    onClick={handleResendOtp}
                    disabled={otpLoading}
                  >
                    Send a new code
                  </button>
                )}
              </div>

              <div className="ark-modal-actions">
                <button
                  type="button"
                  className="ark-btn-ghost"
                  onClick={closeOtpModal}
                  disabled={otpLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="ark-btn ark-btn-sm"
                  disabled={otpLoading || otp.length !== OTP_LENGTH}
                >
                  {otpLoading && (
                    <Loader2 size={16} className="animate-spin" />
                  )}
                  {otpLoading ? "Verifying…" : "Verify and sign in"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;